import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import technicianService from '../../api/technicianService';
import { useNotifications } from '../../context/NotificationContext';
import BookingCard from '../common/BookingCard';
import Pagination from '../common/Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import { TECHNICIAN_BOOKING_ACTIONS } from '../../utils/constants';

const FILTERS = ['', 'PENDING', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'REJECTED'];
const TONES = { CONFIRMED: 'accent', REJECTED: 'danger', CANCELLED: 'danger', COMPLETED: 'accent', IN_PROGRESS: '' };

export default function MyBookings() {
  const { t } = useTranslation();
  const notify = useNotifications();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    technicianService
      .listBookings({ page, size: 10, ...(status ? { status } : {}) })
      .then(setData)
      .catch((e) => notify.apiError(e))
      .finally(() => setLoading(false));
  }, [page, status, notify]);

  useEffect(() => { load(); }, [load]);

  const apply = async (booking, next) => {
    let extra = {};
    if (next === 'COMPLETED') {
      const cost = window.prompt(`${t('booking.final')} (${t('common.optional')})`, booking.estimatedCost ?? '');
      if (cost) extra.actualCost = Number(cost);
    }
    setBusy(true);
    try {
      await technicianService.updateBookingStatus(booking.id, next, extra);
      notify.success(t(`booking.status.${next}`));
      load();
    } catch (e) {
      notify.apiError(e);
    } finally {
      setBusy(false);
    }
  };

  const actionsFor = (b) =>
    (TECHNICIAN_BOOKING_ACTIONS[b.status] || []).map((s) => ({ status: s, tone: TONES[s] }));

  return (
    <div className="page">
      <div className="container stack" style={{ gap: '1.25rem' }}>
        <h1>{t('technician.jobs')}</h1>
        <div className="toolbar">
          {FILTERS.map((f) => (
            <button key={f || 'all'} className={`chip ${status === f ? 'active' : ''}`}
              onClick={() => { setStatus(f); setPage(0); }}>
              {f ? t(`booking.status.${f}`) : t('common.all')}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : data.content.length === 0 ? (
          <EmptyState icon="briefcase" title={t('technician.noJobs')} />
        ) : (
          <div className="stack">
            {data.content.map((b) => (
              <BookingCard key={b.id} booking={b} perspective="technician" actions={actionsFor(b)} busy={busy} onAction={apply} />
            ))}
          </div>
        )}
        <Pagination page={page} totalPages={data.totalPages} onChange={setPage} />
      </div>
    </div>
  );
}
